//* 주문의 bugoDetail(리본 문구)을 변경하기 위한 모달
//* 입금대기 주문에선 "구매자가 문구를 잘못 불러줬다" 응대용으로 쓰이므로,
//* 수정 직후 주문내역 안내(LMS)를 다시 보낼 수 있게 한다.
//* (문구는 주문내역 LMS 본문에만 들어간다 — 계좌 SMS만 재발송하면 고친 문구가 전달되지 않음)

import { FC, useCallback, useEffect, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import PhraseInfoInputs from '@service/store/components/PhraseInfoInputs';
import {
  resendShopOrderAlert,
  updateShopOrder,
} from '@shared/api/shopOrder/shopOrder.controller';
import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { PaymentState } from '@shared/types';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

interface Props {
  openId: string;
  order: ShopOrderForSeller;
}

interface PhraseForm {
  phraseSender: string;
  phraseCondolence: string;
}

//* 입금 안내를 다시 보낼 수 있는 주문인지 (백엔드 waiting 재발송 조건과 동일)
//* 전화주문(Waiting)=고정계좌 / 가상계좌 발급(NotPaid+vBank)
export const canResendWaitingAlert = (order: ShopOrderForSeller) =>
  order.paymentDetail.status === PaymentState.WAITING ||
  (order.paymentDetail.status === PaymentState.NOT_PAID &&
    Boolean(order.paymentDetail.vBank));

export const OrderBugoDetailEditModal: FC<Props> = function OrderBugoDetailEditModal({
  openId,
  order,
}: Props) {
  const { sellerRole } = useAuth();

  const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));
  const [submitting, setSubmitting] = useState(false);

  const isWaitingOrder = canResendWaitingAlert(order);
  //* 입금대기 주문은 고친 문구를 구매자가 다시 확인해야 하므로 기본 발송
  const [resendGuidance, setResendGuidance] = useState(isWaitingOrder);

  const onCloseModal = useCallback(() => {
    setOpen(false);
    history.back();
  }, [setOpen]);

  const { register, control, handleSubmit, reset } = useForm<PhraseForm>({
    defaultValues: {
      phraseSender: order.bugoDetail.senderPhrase,
      phraseCondolence: order.bugoDetail.condolencePhrase,
    },
  });
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const queryClient = useQueryClient();

  //* 열 때마다 최신 문구로 리시드 (닫았다 다시 열면 이전 입력이 남지 않도록)
  useEffect(() => {
    if (open) {
      reset({
        phraseSender: order.bugoDetail.senderPhrase,
        phraseCondolence: order.bugoDetail.condolencePhrase,
      });
      setResendGuidance(isWaitingOrder);
    }
  }, [
    open,
    reset,
    order.bugoDetail.senderPhrase,
    order.bugoDetail.condolencePhrase,
    isWaitingOrder,
  ]);

  const onSubmit: SubmitHandler<PhraseForm> = useCallback(
    async (data) => {
      const senderPhrase = data.phraseSender.trim();
      const condolencePhrase = data.phraseCondolence.trim();
      if (!senderPhrase || !condolencePhrase) {
        notificateError(notifiacationInstance, '문구를 모두 입력해주세요.');
        return;
      }

      setSubmitting(true);
      try {
        await updateShopOrder(
          {
            _id: order._id,
            bugoDetail: {
              senderPhrase,
              condolencePhrase,
            },
          },
          sellerRole(),
        );
      } catch (err: any) {
        console.error(err);
        notificateError(notifiacationInstance, '수정 실패');
        setSubmitting(false);
        return;
      }

      //* 배송관리·미결제·입금대기 목록이 각각 다른 URL이라 shop-order 조회를 통째로 무효화
      await invalidateQueriesByUrlPart(queryClient, 'shop-order');

      //* 저장은 됐으므로 재발송 실패해도 수정 자체는 성공으로 안내한다
      if (resendGuidance && isWaitingOrder) {
        try {
          //* smsOnly=false — 문구가 담긴 주문내역 LMS + 계좌 SMS를 함께 재발송
          await resendShopOrderAlert(order._id, 'waiting', sellerRole(), false);
          notificateSuccess(notifiacationInstance, '문구 수정 · 안내 재발송 완료');
        } catch (err: any) {
          console.error(err);
          const status = err?.response?.status;
          notificateError(
            notifiacationInstance,
            status === 400
              ? '문구는 수정됐지만 주문 상태상 안내를 재발송할 수 없습니다.'
              : status === 404
              ? '문구는 수정됐지만 재발송 기능이 아직 서버에 반영되지 않았습니다.'
              : '문구는 수정됐지만 안내 재발송에 실패했습니다.',
          );
        }
      } else {
        notificateSuccess(notifiacationInstance, '수정 성공');
      }

      setSubmitting(false);
      onCloseModal();
    },
    [
      isWaitingOrder,
      notifiacationInstance,
      onCloseModal,
      order._id,
      queryClient,
      resendGuidance,
      sellerRole,
    ],
  );

  return (
    <Modal openId={openId}>
      <div className="space-y-4">
        <div>
          <p className="text-lg font-bold">문구 수정</p>
          <p className="break-keep pt-1 text-sm text-gray-600">
            리본에 들어갈 보내는분·조의문구를 고칩니다.
          </p>
        </div>
        <PhraseInfoInputs
          register={register}
          control={control}
          names={{
            phraseSender: 'phraseSender',
            phraseCondolence: 'phraseCondolence',
          }}
          //* 모달이라 뒤 목록 페이지가 스크롤될 이유가 없다
          scrollOnFocus={false}
        />
        {isWaitingOrder && (
          <label className="flex cursor-pointer items-start gap-2 rounded-md border bg-gray-50 p-3">
            <input
              type="checkbox"
              className="wh-4 mt-0.5 shrink-0"
              checked={resendGuidance}
              onChange={(e) => setResendGuidance(e.target.checked)}
            />
            <span className="text-sm">
              <span className="font-medium">수정한 문구로 안내 다시 보내기</span>
              <span className="block break-keep pt-0.5 text-xs text-gray-500">
                주문내역(문구 포함) 문자와 입금계좌 안내가 구매자에게 다시 나갑니다.
              </span>
            </span>
          </label>
        )}
        <Button
          className="button-rectangle h-10 theme-text-4 theme-bg-main disabled:opacity-50"
          disabled={submitting}
          onClick={handleSubmit(onSubmit)}
        >
          {submitting ? '처리 중...' : '수정'}
        </Button>
      </div>
    </Modal>
  );
};
